import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// MapComponent with responsive sizing and multiple locators/routes support
import { useEffect, useRef, useCallback } from "react";
import { observer } from "mobx-react-lite";
import { Spin, Alert, Card, Button } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { getRootStore } from "../stores/RootStore";
import styles from "../styles/MapComponent.module.less";
export const MapComponent = observer(({ width = "100%", height = "400px", config, className, style, showLoadingSpinner = true, showErrorAlert = true, showInfo = true, showCard = false, cardTitle = "Карта", }) => {
    const { mapStore } = getRootStore();
    const mapContainerRef = useRef(null);
    const isInitializingRef = useRef(false);
    // Container size management
    const containerSize = { width, height };
    const initializeMap = useCallback(async () => {
        if (mapContainerRef.current && !isInitializingRef.current) {
            // Check if map is initialized for a different container
            if (mapStore.isInitialized &&
                mapStore.mapContainer !== mapContainerRef.current) {
                mapStore.destroy();
            }
            if (!mapStore.isInitialized) {
                isInitializingRef.current = true;
                try {
                    // Update container size in store
                    mapStore.setContainerSize({ width, height });
                    // Initialize map
                    await mapStore.initializeMap(mapContainerRef.current, config);
                }
                catch (error) {
                    console.error("Failed to initialize map:", error);
                }
                finally {
                    isInitializingRef.current = false;
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapStore]);
    const handleRetry = useCallback(() => {
        mapStore.clearMapError();
        mapStore.destroy();
        initializeMap();
    }, [mapStore, initializeMap]);
    // Initialize map once when container is ready
    useEffect(() => {
        // Small delay to ensure DOM is ready
        const timeoutId = setTimeout(() => {
            initializeMap();
        }, 100);
        return () => {
            clearTimeout(timeoutId);
            // Reset initialization flag on unmount
            isInitializingRef.current = false;
        };
    }, [initializeMap]);
    // Handle container size changes
    useEffect(() => {
        if (mapStore.isInitialized) {
            mapStore.setContainerSize(containerSize);
        }
    }, [width, height, mapStore]);
    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            if (mapStore.isInitialized) {
                mapStore.resizeMap();
            }
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [mapStore]);
    // Cleanup on unmount
    useEffect(() => {
        return () => {
            // Reset initialization flag to ensure proper cleanup
            isInitializingRef.current = false;
            // Note: We don't destroy the store here as it might be used by other components
            // The store should be destroyed when the application unmounts
        };
    }, []);
    const renderMapContainer = () => (_jsxs("div", { className: styles.mapWrapper, style: { width, height }, children: [showLoadingSpinner && mapStore.isLoading && (_jsx("div", { className: styles.loadingOverlay, children: _jsx(Spin, { size: "large", tip: "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430 \u043A\u0430\u0440\u0442\u044B..." }) })), showErrorAlert && mapStore.error && (_jsx("div", { className: styles.errorOverlay, children: _jsx(Alert, { type: "error", message: "\u041E\u0448\u0438\u0431\u043A\u0430 \u043A\u0430\u0440\u0442\u044B", description: mapStore.error, showIcon: true, closable: true, onClose: () => {
                        mapStore.clearMapError();
                    }, action: _jsx(Button, { size: "small", icon: _jsx(ReloadOutlined, {}), onClick: handleRetry, loading: mapStore.isLoading, children: "\u041F\u043E\u0432\u0442\u043E\u0440\u0438\u0442\u044C" }) }) })), _jsx("div", { ref: mapContainerRef, className: `${styles.mapContainer} ${className || ""}`, style: {
                    width: "100%",
                    height: "100%",
                    ...style,
                } }), showInfo && mapStore.isInitialized &&
                (mapStore.hasLocators || mapStore.hasRoutes) && (_jsxs("div", { className: styles.mapInfo, children: [_jsxs("div", { className: styles.infoItem, children: [_jsx("span", { className: styles.infoLabel, children: "\u041B\u043E\u043A\u0430\u0442\u043E\u0440\u044B:" }), _jsx("span", { className: styles.infoValue, children: mapStore.visibleLocators.length })] }), _jsxs("div", { className: styles.infoItem, children: [_jsx("span", { className: styles.infoLabel, children: "\u041C\u0430\u0440\u0448\u0440\u0443\u0442\u044B:" }), _jsx("span", { className: styles.infoValue, children: mapStore.visibleRoutes.length })] }), _jsxs("div", { className: styles.infoItem, children: [_jsx("span", { className: styles.infoLabel, children: "\u0422\u043E\u0447\u0435\u043A:" }), _jsx("span", { className: styles.infoValue, children: mapStore.totalPoints })] })] }))] }));
    // Render with or without card wrapper
    if (showCard) {
        return (_jsx(Card, { title: cardTitle, className: `${styles.mapCard} ${className || ""}`, style: style, styles: { body: { padding: 0 } }, children: renderMapContainer() }));
    }
    return renderMapContainer();
});
// Display name for debugging
MapComponent.displayName = "MapComponent";
